<template>
  <div class="pc__wrapper">
    <div
      :class="['pc__images', { OOS: !product.available && showOOSbadge }, { selected: isSelected }]"
      :data-stio-gtm-click-id="gtmTrackId != '' ? gtmTrackId : null"
      :data-ctl-click="isTrackClickEvent"
      @click="handleProductCardClick"
      tabindex="0"
      role="button"
      @keydown="handleKeydownOnProductCardClick"
    >
      <!-- Check mark circle icon on selected product image -->
      <svg v-if="isSelected" class="pc__check-mark-icon">
        <use xlink:href="#check-mark-circle"></use>
      </svg>

      <div v-if="!product.available && showOOSbadge" class="pc__oos-badge">SOLD OUT</div>
      <ProductImage
        :enableImageCarousel="enableImageCarousel"
        :selectedColor="selectedColor"
        :product="product"
        :isLoadingLazy="isLoadingLazy"
      />
      <WishlistCTA
        v-if="showWishlistIcon"
        :product="product"
        :selectedVariantDetails="{ Color: selectedColor }"
        :firstOptionName="firstKeyName"
        :showOptions="true"
        customClass="product-card_wishlisticon"
      />
    </div>
    <div :class="['pc__info', { 'pc__info--secondTurn': isSecondTurn }]">
      <div class="pc__info-inner">
        <Swatch
          v-if="!isSecondTurn && selectedColor"
          :colorOptions="renderColorOptions"
          :selectedColor="selectedColor"
          :isLoadingLazy="isLoadingLazy"
          @color-change="updateSelectedColor"
        />
        <div :class="['pc__product-title', { 'pc__product-title--margin': !selectedColor }]" role="heading" aria-level="2">{{ product.title }}</div>
        <div v-if="badge && badge.type === 'Image'" class="pc__sticker">
          {{ badge.tag.toLowerCase().replace('callout: ', '') }}
        </div>
        <div v-if="enableReviews" :class="['pc__reviews', { loading: loadMoreProducts }]" tabindex="-1">
          <div
            v-if="!isSecondTurn"
            class="yotpo-widget-instance"
            data-yotpo-instance-id="498880"
            :data-yotpo-product-id="product.id"
          ></div>
        </div>
        <div class="pc__price-wrapper">
          <div :class="['pc__price', { bold: comparePrice > price }]">
            <span v-if="isSecondTurn">Used</span> {{ formatPrice(price) }}
          </div>
          <div v-if="isPriceRange && customerDiscountVal <= 0" class="pc__price-range">{{ isPriceRange }}</div>
          <div v-else-if="comparePrice > price" class="pc__compare-price">
            {{ formatPrice(comparePrice) }}
          </div>
        </div>
      </div>
      <button @click="handleViewProductDetailsClick" class="btn btn--regular pc__product-details-cta">
        View product details
      </button>
    </div>
  </div>
</template>

<script setup>
  /** ProductCardTypeE is used in Build Your Kit page */
  import { computed, toRefs, ref, onBeforeMount, reactive, watch } from 'vue';
  import WishlistCTA from '../wishlist-cta.vue';
  import Swatch from './product-swatch.vue';
  import ProductImage from './product-image.vue';
  import productCardProps from './product-card-props';

  const props = defineProps({
    isSecondTurn: productCardProps.isSecondTurn,
    loadMoreProducts: productCardProps.loadMoreProducts,
    product: productCardProps.product,
    callouts: productCardProps.callouts,
    showOOSbadge: productCardProps.showOOSbadge,
    gtmTrackId: productCardProps.gtmTrackId,
    isLoadingLazy: productCardProps.isLoadingLazy,
    isTrackClickEvent: productCardProps.isTrackClickEvent,
    enableImageCarousel: productCardProps.enableImageCarousel,
    enableReviews: productCardProps.enableReviews,
    selectedColorValue: productCardProps.selectedColorValue,
    showWishlistIcon: productCardProps.showWishlistIcon,
    layer: productCardProps.layer,
    subcategory: productCardProps.subcategory,
    selectedProducts: productCardProps.selectedProducts,
  });

  const { isSecondTurn, product, callouts, layer, subcategory, selectedColorValue } = toRefs(props);

  const emit = defineEmits(['product-click', 'view-product-details']);

  const selectedProductsObject = reactive(props.selectedProducts);
  const firstKeyName = reactive(product.value.options[0].name);
  const selectedColor = ref(null);
  const customerDiscountVal = ref(0);

  onBeforeMount(() => {
    initialSetup();
    getCustomerDiscount();
  });

  watch(product, (value) => {
    initialSetup();
  });

  const isSelected = computed(() => {
    return bykSelectedProducts.value ? bykSelectedProducts.value?.[0]?.id === product.value?.id : false;
  });

  const badge = computed(() => {
    if (callouts.value.length) {
      const badgeTag = callouts.value.filter((obj) =>
        product.value.tags.some((value) => value.toLowerCase() === obj.tag.toLowerCase())
      );

      return badgeTag.length ? badgeTag[0] : false;
    } else {
      return false;
    }
  });

  const featureImage = computed(() => {
    const { images, title } = product.value;

    const imageData = window.getProductImages(images, selectedColor.value, title);
    return imageData[0];
  });

  const activeColorData = computed(() => {
    return selectedColor.value && renderColorOptions.value[selectedColor.value];
  });

  const price = computed(() => {
    if (activeColorData.value && activeColorData.value.comparePrice > 0) {
      const { comparePrice, price } = activeColorData.value;

      if (comparePrice * (1 - customerDiscountVal.value) < price) {
        return comparePrice * (1 - customerDiscountVal.value);
      } else {
        return price;
      }
    } else {
      return (product.value.price / 100) * (1 - customerDiscountVal.value);
    }
  });

  const comparePrice = computed(() => {
    if (activeColorData.value) {
      if (activeColorData.value.comparePrice > 0) {
        return activeColorData.value.comparePrice;
      } else {
        return activeColorData.value.price;
      }
    } else {
      return product.value.compare_at_price / 100;
    }
  });

  const isPriceRange = computed(() => {
    const { min_price, max_price } = product.value;

    if (min_price != max_price) {
      return `(${formatPrice(min_price / 100)} - ${formatPrice(max_price / 100)})`;
    } else {
      return false;
    }
  });

  const renderColorOptions = computed(() => {
    const { variants, tags, images, available } = product.value;

    const firstLevelKeys = variants.reduce((set, variant) => {
      const property = variant.option1;
      const setItem = (set[property] = set[property] || { items: [] });
      setItem.items.push(variant);
      if (variant.available) setItem.items.available = true;
      return set;
    }, {});

    Object.entries(firstLevelKeys).forEach(([key, value]) => {
      const isAvailable = value.items.available;
      const bisData = tags.filter((tag) => tag.toLowerCase().includes(`bis: ${key.toLowerCase()}`));

      if (isAvailable || bisData.length || !available) {
        const imagePattern = new RegExp(`^\\s*${key.toLowerCase()}\\s*swatch\\s*$`);
        value.image = images.filter((image) => {
          const alt = image.alt.toLowerCase();
          return alt && imagePattern.test(alt);
        })[0];

        value.price = Number(value.items[0].price / 100);
        value.comparePrice = Number(value.items[0].compare_at_price / 100);
      } else {
        delete firstLevelKeys[key];
      }
    });
    return firstLevelKeys;
  });

  const bykSelectedProducts = computed(() => {
    return selectedProductsObject?.[layer.value]?.[subcategory.value] || [];
  });

  const initialSetup = () => {
    if (renderColorOptions.value && firstKeyName === 'Color') {
      // This needs to be refactor we can select on the basis of some flag
      selectedColor.value =
        selectedColorValue.value || getLastSelectedSwatch() || Object.keys(renderColorOptions.value)[0];
    }
  };

  const updateSelectedColor = (color) => {
    selectedColor.value = color;
    handleProductCardClick(undefined, true);
  };

  const getCustomerDiscount = () => {
    customerDiscountVal.value = window.customerDiscount(product.value.tags);
  };

  const formatPrice = (value) => {
    return window.formattedPrice(value);
  };

  const handleProductCardClick = (event, selectedUsingSwatch = false) => {
    const updatedProduct = {
      ...product.value,
      selectedVariantDetails: {
        selectedFeatureImage: featureImage.value,
        color: selectedColor.value,
        price: price.value,
        comparePrice: comparePrice.value,
      },
    };

    let productToEmit;

    if (selectedUsingSwatch) {
      // Only emit the product if it's selected when using swatch
      if (isSelected.value) {
        productToEmit = updatedProduct;
      } else {
        return; // Don't dispatch anything if isSelected is false
      }
    } else {
      // When not using swatch, emit undefined if selected to deselect the product, otherwise the new product
      productToEmit = isSelected.value ? undefined : updatedProduct;
    }

    // Emit the result with product, layer, and subcategory
    emit('product-click', {
      product: productToEmit,
      layer: layer.value,
      subcategory: subcategory.value,
    });
  };

  const handleKeydownOnProductCardClick = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handleProductCardClick(event);
    }
  };

  const handleViewProductDetailsClick = () => {
    emit('view-product-details', {
      product: {
        ...product.value,
        selectedVariantDetails: {
          selectedFeatureImage: featureImage.value,
          color: selectedColor.value,
          price: price.value,
          comparePrice: comparePrice.value,
        },
      },
      selectedColor: selectedColor.value,
      layer: layer.value,
      subcategory: subcategory.value,
    });
  };

  const getLastSelectedSwatch = () => {
    let bykSelectedColor;
    if (product.value?.id === bykSelectedProducts.value?.[0]?.id) {
      bykSelectedColor = bykSelectedProducts.value?.[0]?.selectedVariantDetails?.color || '';
    }

    return bykSelectedColor;
  };
</script>
